import { LinearProgress } from '@material-ui/core'
import withWidth from '@material-ui/core/withWidth'
import { CameraIcon, Dialog, DialogContent, DialogTitle, styled, Typography, UploadIcon } from 'opensolar-ui'

import { Button, DialogActions } from 'opensolar-ui'
import React, { useCallback } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { OpenSolarPalette } from 'Themes'
import { UploadButton } from './components/UploadButton'
import { UploadFileContainer } from './components/UploadFileContainer'
import { UploadFileDropzone } from './components/UploadFileDropzone'
import { UploadFileList } from './components/UploadFileList'
import { UploadFileTags } from './components/UploadFileTags'
import { FileRecord, ResourcePrefix, UploadFileProvider, useFileUploadContext } from './controller/UploadFileContext'
import { ContentWrapper, LoadingOverlay } from './styles'
import { imgOrPdfTypeExtensionsAsString } from './utils'

const ErrorArea = styled('div')(({ theme }) => ({
  marginBottom: 14,
}))

export const FileInputContainer = styled('div')(({ theme }) => ({
  overflow: 'hidden',
  marginTop: -20,
  marginBottom: 15,
}))

type PropTypes = {
  isOpen: boolean
  onDismiss: () => void
  projectId?: string
  resource_prefix?: ResourcePrefix
  projectUrl?: string
  orgId: number
  onSuccess?: (uploadedFiles: FileRecord[]) => void
  fileType?: 'file' | 'image'
  trackEventExtras?: Record<string, any>
  filesInForm?: any[]
  defaultFileTagTitles?: string[]
  hideTags?: boolean
  closeOnSuccess?: boolean
}

const MultipleFilesDialogInner: React.FunctionComponent<PropTypes> = (props) => {
  const resource_prefix = props.resource_prefix || 'private'
  const fileType = props.fileType || 'file'
  const fileTypesAccepted = fileType === 'file' ? '' : imgOrPdfTypeExtensionsAsString
  const placeholderIcon = fileType === 'file' ? <UploadIcon /> : <CameraIcon />
  const notify = useNotify()
  const translate = useTranslate()

  const dismiss = useCallback(() => {
    props.onDismiss()
  }, [props])

  const handleUploadSuccess = useCallback(
    (uploadedFiles: FileRecord[]) => {
      const filesInForm = props.filesInForm || []
      const urlsInForm = filesInForm.map((f) => f?.url)
      const allInForm = uploadedFiles.every((f) => {
        const url = f.fileRecord?.url
        return url && urlsInForm.includes(url)
      })
      if (allInForm) {
        notify('Your changes have been saved', 'success')
      } else {
        notify('Your file(s) have been uploaded', 'success')
      }
      props.onSuccess?.(uploadedFiles)
      if (props.closeOnSuccess !== false) {
        dismiss()
      }
    },
    [props, notify, dismiss]
  )

  const handleUploadError = useCallback(
    (error: Error) => {
      notify(error.message, 'warning')
    },
    [notify]
  )

  return (
    <UploadFileProvider projectUrl={props.projectUrl} orgId={props.orgId} projectId={props.projectId}>
      <DialogInner
        isOpen={props.isOpen}
        onDismiss={dismiss}
        fileType={fileType}
        fileTypesAccepted={fileTypesAccepted}
        placeholderIcon={placeholderIcon}
        resource_prefix={resource_prefix}
        translate={translate}
        onUploadSuccess={handleUploadSuccess}
        onUploadError={handleUploadError}
        defaultFileTagTitles={props.defaultFileTagTitles}
        hideTags={props.hideTags}
        closeOnSuccess={props.closeOnSuccess}
        orgId={props.orgId}
      />
    </UploadFileProvider>
  )
}

const DialogInner: React.FunctionComponent<{
  isOpen: boolean
  onDismiss: () => void
  fileType: 'file' | 'image'
  fileTypesAccepted: string
  placeholderIcon: React.ReactNode
  resource_prefix: ResourcePrefix
  translate: (key: string) => string
  onUploadSuccess: (uploadedFiles: FileRecord[]) => void
  onUploadError: (error: Error) => void
  defaultFileTagTitles?: string[]
  hideTags?: boolean
  closeOnSuccess?: boolean
  orgId: number
}> = ({
  isOpen,
  onDismiss,
  fileType,
  fileTypesAccepted,
  placeholderIcon,
  resource_prefix,
  translate,
  onUploadSuccess,
  onUploadError,
  defaultFileTagTitles,
  hideTags,
  closeOnSuccess,
  orgId,
}) => {
  const { uploadStatus, totalFilesCount, resetUpload } = useFileUploadContext()

  const handleUploadSuccess = (uploadedFiles: FileRecord[]) => {
    onUploadSuccess(uploadedFiles)
    if (closeOnSuccess === false) {
      resetUpload()
    }
  }

  return (
    <Dialog
      open={isOpen}
      onBackdropClick={onDismiss}
      fullWidth
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
    >
      {uploadStatus === 'uploading' && (
        <LoadingOverlay>
          <LinearProgress variant="indeterminate" />
        </LoadingOverlay>
      )}
      <DialogTitle>
        <span>{translate(fileType === 'file' ? 'Upload Document' : 'Upload Site Photos')}</span>
      </DialogTitle>
      <DialogContent>
        <UploadFileContainer>
          <ContentWrapper>
            {uploadStatus === 'uploadFailed' && (
              <ErrorArea>
                <Typography variant="h6" colorHex={OpenSolarPalette.errorRed}>
                  {translate('There was an error uploading your files.')}
                </Typography>
                <Typography variant="subtitle2">
                  {translate('Click "Save" to try again or "Cancel" to discard your changes')}
                </Typography>
              </ErrorArea>
            )}

            <FileInputContainer>
              <UploadFileList
                slot="file_contents"
                resource_prefix={resource_prefix}
                uploadStatus={uploadStatus}
                dropzone={
                  <UploadFileDropzone
                    slot="file_contents"
                    uploadStatus={uploadStatus}
                    fileTypesAccepted={fileTypesAccepted}
                    placeholderIcon={placeholderIcon}
                    multiple={true}
                    resource_prefix={resource_prefix}
                  />
                }
              />
            </FileInputContainer>

            <UploadFileTags
              resource_prefix={resource_prefix}
              slot="file_contents"
              disabled={uploadStatus === 'uploading'}
              defaultFileTagTitles={defaultFileTagTitles}
              hideTags={hideTags}
              orgId={orgId}
            />
          </ContentWrapper>
        </UploadFileContainer>
      </DialogContent>

      <DialogActions>
        <Button onClick={onDismiss} variant="contained" color="default">
          <span>{translate('Cancel')}</span>
        </Button>

        <UploadButton
          label={translate('Save')}
          disabled={totalFilesCount === 0}
          onSuccess={handleUploadSuccess}
          onError={onUploadError}
        />
      </DialogActions>
    </Dialog>
  )
}
export const MultipleFilesDialog = withWidth()(MultipleFilesDialogInner)
